import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type QueryParam<TOption extends string> = {
  param: string;
  options: readonly [TOption, ...(readonly TOption[])];
  validateOption: (option: string | null) => TOption | null;
  clearOtherParams?: boolean;
};

const clearParams = (params: URLSearchParams) => {
  params.forEach((_, key) => params.delete(key));
};

export function useQueryParamOption<TOption extends string>({
  param,
  options,
  validateOption,
  clearOtherParams = false,
}: QueryParam<TOption>) {
  const defaultOption = options[0];
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = useCallback(
    (input: string | null) => {
      const option = validateOption(input);
      const currentOption = searchParams.get(param);

      if (option === defaultOption || !option) {
        setSearchParams(
          (params) => {
            if (clearOtherParams) clearParams(params);
            params.delete(param);
            return params;
          },
          { replace: true },
        );
      } else if (currentOption !== option) {
        setSearchParams(
          (params) => {
            if (clearOtherParams) clearParams(params);
            params.set(param, option);
            return params;
          },
          { replace: true },
        );
      }
    },
    [
      clearOtherParams,
      defaultOption,
      param,
      searchParams,
      setSearchParams,
      validateOption,
    ],
  );

  return useMemo(() => {
    return [
      validateOption(searchParams.get(param)),
      (option: TOption | undefined) => {
        setParam(option || null);
      },
    ] as const;
  }, [param, searchParams, setParam, validateOption]);
}
